import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUpdateLessonProgress } from './useUpdateLessonProgress';
import {
  clearStoredLessonResumeSeconds,
  getLessonResumeSeconds,
  setStoredLessonResumeSeconds,
} from '../utils/lessonProgressStorage';

const PROGRESS_SYNC_INTERVAL_MS = 8000;
const COMPLETION_THRESHOLD = 0.98;

export function useLessonPlaybackSync({
  lessonId,
  resumeAtSeconds = 0,
  onSynced,
  onComplete,
}) {
  const { saveProgress } = useUpdateLessonProgress();
  const lastPlayerTimeRef = useRef(0);
  const completedRef = useRef(false);
  const syncInFlightRef = useRef(false);
  const throttleTimerRef = useRef(null);

  const initialResumeAt = useMemo(() => {
    if (!lessonId) return 0;
    return getLessonResumeSeconds(lessonId, resumeAtSeconds);
  }, [lessonId, resumeAtSeconds]);

  useEffect(() => {
    lastPlayerTimeRef.current = initialResumeAt;
    completedRef.current = false;

    return () => {
      if (throttleTimerRef.current) {
        window.clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
    };
  }, [lessonId, initialResumeAt]);

  const applySyncResponse = useCallback(
    (response, { completed = false } = {}) => {
      const payload = response?.data ?? response ?? {};
      const watchedSeconds = payload.watched_seconds;
      const isCompleted = Boolean(payload.completed || completed);

      if (watchedSeconds != null) {
        setStoredLessonResumeSeconds(lessonId, watchedSeconds);
      }

      if (isCompleted) {
        completedRef.current = true;
        clearStoredLessonResumeSeconds(lessonId);
      }

      onSynced?.({
        watchedSeconds: watchedSeconds ?? undefined,
        completed: isCompleted,
        videoDurationSeconds: payload.duration_seconds ?? undefined,
      });

      if (isCompleted) {
        onComplete?.();
      }
    },
    [lessonId, onComplete, onSynced]
  );

  const syncDelta = useCallback(
    async (currentTime, { completed = false, force = false } = {}) => {
      if (!lessonId || completedRef.current) return null;
      if (syncInFlightRef.current) return null;

      const current = Math.max(0, Number(currentTime) || 0);
      const delta = Math.max(0, Math.round(current - lastPlayerTimeRef.current));

      if (!force && delta <= 0 && !completed) return null;

      syncInFlightRef.current = true;

      try {
        const response = await saveProgress(lessonId, {
          seconds_watched: delta > 0 ? delta : 0,
          completed,
        });

        if (response?.success !== false) {
          lastPlayerTimeRef.current = current;
          applySyncResponse(response, { completed });
        }

        return response;
      } finally {
        syncInFlightRef.current = false;
      }
    },
    [applySyncResponse, lessonId, saveProgress]
  );

  const handleTimeUpdate = useCallback(
    (currentTime, duration) => {
      if (!lessonId || completedRef.current) return;

      setStoredLessonResumeSeconds(lessonId, currentTime);

      const totalDuration = Number(duration) || 0;
      if (totalDuration > 0 && currentTime / totalDuration >= COMPLETION_THRESHOLD) {
        void syncDelta(currentTime, { completed: true, force: true });
        return;
      }

      if (throttleTimerRef.current) return;

      throttleTimerRef.current = window.setTimeout(() => {
        throttleTimerRef.current = null;
        void syncDelta(currentTime);
      }, PROGRESS_SYNC_INTERVAL_MS);
    },
    [lessonId, syncDelta]
  );

  const handlePause = useCallback(
    (currentTime) => {
      if (throttleTimerRef.current) {
        window.clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
      void syncDelta(currentTime, { force: true });
    },
    [syncDelta]
  );

  const handleEnded = useCallback(
    (currentTime, duration) => {
      if (completedRef.current) return;
      const endTime = Number(duration) > 0 ? Number(duration) : Number(currentTime) || 0;
      void syncDelta(endTime, { completed: true, force: true });
    },
    [syncDelta]
  );

  const handleDurationKnown = useCallback(
    (durationSeconds) => {
      if (!lessonId || !durationSeconds) return;
      onSynced?.({ videoDurationSeconds: durationSeconds });
    },
    [lessonId, onSynced]
  );

  return {
    initialResumeAt,
    handleTimeUpdate,
    handlePause,
    handleEnded,
    handleDurationKnown,
  };
}
